/**
 * Ontologia editável de interações clínicas.
 *
 * A matriz é armazenada dentro de interaction_rules.matrix de cada canônico.
 * O motor usa a regra apenas quando os dois agentes estão presentes e refina
 * a relevância pelo tempo desde a última exposição registrada.
 */

export type MatrixEvidenceGrade = "A" | "B" | "C" | "D" | "desconhecida";
export type MatrixDirection = "aumenta" | "reduz" | "bidirecional" | "antagonismo_funcional" | "incerto";
export type MatrixTemporalRelevance = "aguda" | "residual" | "manutencao" | "qualquer";

export interface ClinicalInteractionMatrixRule {
  id: string;
  match_a: string;
  match_b: string;
  category?: "farmacodinamica" | "farmacocinetica" | "metabolica" | "contextual" | "alimentar" | "comportamental";
  pd_axis?: string;
  pk_mechanism?: string;
  direction?: MatrixDirection;
  effect?: string;
  mechanism?: string;
  interpretation?: string;
  phenotype?: string[];
  monitor?: string[];
  red_flags?: string[];
  action?: string;
  severity?: "informativa" | "monitorar" | "cautela" | "relevante" | "urgente";
  confidence?: "baixa" | "moderada" | "alta";
  evidence_grade?: MatrixEvidenceGrade;
  source?: string;
  source_url?: string;
  temporal_relevance?: MatrixTemporalRelevance;
  acute_window_hours?: number;
  residual_window_hours?: number;
  maintenance_relevant?: boolean;
  dose_dependence?: "baixa" | "moderada" | "alta" | "desconhecida";
  patient_modifiers?: string[];
  route_modifiers?: string[];
  formulation_modifiers?: string[];
  notes?: string;
}

export function normalizeInteractionToken(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function tokenMatches(value: string, matcher: string): boolean {
  const v = normalizeInteractionToken(value);
  const m = normalizeInteractionToken(matcher);
  if (!m || m === "*") return true;
  return v === m || v.includes(m) || m.split(" ").every((part) => v.includes(part));
}

export function getInteractionMatrixRules(raw: unknown): ClinicalInteractionMatrixRule[] {
  let value: any = raw;
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return []; }
  }
  if (Array.isArray(value)) value = { matrix: value };
  const rows = Array.isArray(value?.matrix)
    ? value.matrix
    : Array.isArray(value?.rules)
      ? value.rules
      : [];
  return rows
    .filter((r: any) => r && typeof r === "object" && String(r.match_a ?? "").trim() && String(r.match_b ?? "").trim())
    .map((r: any, index: number) => ({
      id: String(r.id ?? `matrix-${index + 1}`),
      match_a: String(r.match_a),
      match_b: String(r.match_b),
      category: r.category ?? "farmacodinamica",
      pd_axis: r.pd_axis,
      pk_mechanism: r.pk_mechanism,
      direction: r.direction,
      effect: r.effect,
      mechanism: r.mechanism,
      interpretation: r.interpretation,
      phenotype: Array.isArray(r.phenotype) ? r.phenotype : [],
      monitor: Array.isArray(r.monitor) ? r.monitor : [],
      red_flags: Array.isArray(r.red_flags) ? r.red_flags : [],
      action: r.action,
      severity: r.severity ?? "monitorar",
      confidence: r.confidence ?? "moderada",
      evidence_grade: r.evidence_grade ?? "desconhecida",
      source: r.source,
      source_url: r.source_url,
      temporal_relevance: r.temporal_relevance ?? "qualquer",
      acute_window_hours: Number.isFinite(Number(r.acute_window_hours)) ? Number(r.acute_window_hours) : 6,
      residual_window_hours: Number.isFinite(Number(r.residual_window_hours)) ? Number(r.residual_window_hours) : 48,
      maintenance_relevant: r.maintenance_relevant !== false,
      dose_dependence: r.dose_dependence ?? "desconhecida",
      patient_modifiers: Array.isArray(r.patient_modifiers) ? r.patient_modifiers : [],
      route_modifiers: Array.isArray(r.route_modifiers) ? r.route_modifiers : [],
      formulation_modifiers: Array.isArray(r.formulation_modifiers) ? r.formulation_modifiers : [],
      notes: r.notes,
    }));
}

export function ruleMatchesPair(rule: ClinicalInteractionMatrixRule, a: string, b: string): boolean {
  return (
    (tokenMatches(a, rule.match_a) && tokenMatches(b, rule.match_b)) ||
    (tokenMatches(a, rule.match_b) && tokenMatches(b, rule.match_a))
  );
}

export function temporalizeMatrixRule(
  rule: ClinicalInteractionMatrixRule,
  hoursSinceLatestExposure: number,
): { include: boolean; severity: ClinicalInteractionMatrixRule["severity"]; temporalNote?: string } {
  const h = Math.max(0, hoursSinceLatestExposure);
  const acute = rule.acute_window_hours ?? 6;
  const residual = rule.residual_window_hours ?? 48;
  let severity = rule.severity ?? "monitorar";

  if (h <= acute) return { include: true, severity };

  if (h <= residual) {
    if (severity === "urgente") severity = "relevante";
    else if (severity === "relevante") severity = "cautela";
    return {
      include: true,
      severity,
      temporalNote: "Exposição fora do pico agudo; leitura atual prioriza cauda/carry-over.",
    };
  }

  if (rule.temporal_relevance === "aguda" || !rule.maintenance_relevant) {
    return { include: false, severity };
  }

  if (severity === "urgente") severity = "relevante";
  else if (severity === "relevante") severity = "cautela";
  else if (severity === "cautela") severity = "monitorar";

  return {
    include: true,
    severity,
    temporalNote: "Exposição de manutenção/carry-over; interação mantida como contexto, sem tratá-la como pico agudo.",
  };
}
